import { useCallback, useMemo } from "react";

import { apiSlice } from "fond/api/apiSlice";
import { useDeleteLayerMutation } from "fond/api/layersSlice";
import { refreshTileSource } from "fond/map/redux";
import { DesignLayers, MuPowerSolverLayerIds } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

// Single source of truth for which solver layers count as "overrides".
// Add new override layer keys here as they are introduced.
const OVERRIDE_LAYER_KEYS = [MuPowerSolverLayerIds.primaryFootprint, MuPowerSolverLayerIds.secondaryFootprint] as const;

export const useOverrides = (solverLayers: DesignLayers) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const [deleteLayer] = useDeleteLayerMutation();

  const overrideLayerIds = useMemo(
    () => OVERRIDE_LAYER_KEYS.map((key) => solverLayers.get(key)?.layer?.ID).filter((id): id is string => !!id),
    [solverLayers]
  );

  const isOverridesSet = overrideLayerIds.length > 0;

  const clearOverrides = useCallback(async () => {
    if (overrideLayerIds.length === 0) return;
    await Promise.all(overrideLayerIds.map((id) => deleteLayer(id).unwrap()));
    dispatch(refreshTileSource("project-layers"));
    dispatch(
      apiSlice.util.invalidateTags([
        { type: "FeatureTotals", id: versionId },
        { type: "Layers", id: versionId },
        { type: "VersionRootConfiguration", id: versionId },
        { type: "ImportStatus", id: versionId },
      ])
    );
  }, [overrideLayerIds, deleteLayer, dispatch, versionId]);

  return { isOverridesSet, clearOverrides };
};
